<template>
  <div v-if="hasWarnings" class="price-warnings">
    <v-tooltip bottom max-width="400" content-class="price-warnings-tooltip">
      <template v-slot:activator="{ on, attrs }">
        <v-icon
          v-bind="attrs"
          size="1.5rem"
          class="price-warnings-icon"
          aria-label="Price explanation warnings"
          v-on="on"
        >
          mdi-help-circle
        </v-icon>
      </template>
      <div class="price-warnings-content">
        <p class="price-warnings-prefix mb-2">
          {{ $t('gridView.priceWarnings.prefix') }}
        </p>
        <ul class="price-warnings-list">
          <li v-for="(warning, key) in structuredWarnings" :key="key" class="price-warnings-item">
            {{ getWarningMessage(key, warning) }}
          </li>
        </ul>
      </div>
    </v-tooltip>
  </div>
</template>

<script>
import { isEmpty, isNumber, isPlainObject, pickBy } from 'lodash';

export default {
  name: 'PriceWarnings',
  localizationKey: 'gridView.priceWarnings',
  props: {
    /**
     * Warnings object from scenario results.
     * Keys are warning types (e.g., 'vatChange', 'exactCompetitorMatch').
     * Values should be objects with structured data for translation interpolation.
     * Legacy string format warnings are filtered out and not displayed.
     */
    warnings: {
      type: Object,
      default: () => ({}),
    },
  },
  computed: {
    /**
     * Filters warnings to only include the latest structured warning by generatedAt timestamp.
     * Legacy string format warnings are excluded as they may be truncated.
     * ISO 8601 timestamp strings are compared lexicographically, naturally preserving microsecond precision.
     *
     * @returns {object} Warnings containing only the latest warning.
     */
    structuredWarnings() {
      const filtered = pickBy(this.warnings, warning => isPlainObject(warning) && !warning.message);

      if (isEmpty(filtered)) {
        return {};
      }

      // Find the warning with the latest generatedAt timestamp
      const entries = Object.entries(filtered);
      const latest = entries.reduce((maxEntry, currentEntry) => {
        return currentEntry[1].generatedAt > maxEntry[1].generatedAt ? currentEntry : maxEntry;
      });

      return { [latest[0]]: latest[1] };
    },
    /**
     * Determines if there are any warnings to display.
     *
     * @returns {boolean} True if there are structured warnings.
     */
    hasWarnings() {
      return !isEmpty(this.structuredWarnings);
    },
  },
  methods: {
    /**
     * Formats a number for display in warning messages.
     *
     * @param {number} value - The number to format.
     * @param {number} decimals - Number of decimal places.
     * @returns {string} The formatted number.
     */
    formatValue(value, decimals = 2) {
      if (!isNumber(value)) {
        return value;
      }
      return value.toFixed(decimals);
    },
    /**
     * Formats referenceDateOrLivePrice, displaying 'missing' translation when null.
     *
     * @param {number} value - The price value to format.
     * @returns {string} The formatted price or translated 'missing' text.
     */
    formatReferenceDateOrLivePrice(value) {
      if (value === null) {
        return this.$t('general.missing');
      }
      return this.formatValue(value);
    },
    /**
     * Formats a VAT rate for display, using 2 decimals when possible, 3 when needed.
     *
     * @param {number} rate - The VAT rate to format.
     * @returns {string|*} The formatted VAT rate, or the original value if not a number.
     */
    formatVatRate(rate) {
      if (!isNumber(rate)) {
        return rate;
      }
      return Number.isInteger(rate * 100) ? rate.toFixed(2) : rate.toFixed(3);
    },
    /**
     * Generates a human-readable description of the deciding factor
     * Displays only the deciding factor field that determined anchor selection.
     *
     * @param {object} criteria - The criteria object containing decidingFactor field.
     * @returns {string} Formatted deciding factor description.
     */
    formatDecidingFactor(criteria) {
      const decidingFactor = criteria.decidingFactor;

      if (!decidingFactor) {
        return '';
      }

      switch (decidingFactor) {
        case 'hasSalesWeightedPenalties':
          return this.$tkey('anchorProductSelected.criteria.hasSalesWeightedPenalties');
        case 'isManualAnchor':
          return this.$tkey('anchorProductSelected.criteria.isManualAnchor');
        case 'salesRank':
          return this.$tkey('anchorProductSelected.criteria.salesRank');
        case 'hasIntentionPrice':
          return this.$tkey('anchorProductSelected.criteria.hasIntentionPrice');
        case 'hasDistribution':
          return this.$tkey('anchorProductSelected.criteria.hasDistribution');
        case 'productKeyDisplay':
          return this.$tkey('anchorProductSelected.criteria.productKeyDisplay', {
            productKeyDisplay: criteria.productKeyDisplay,
          });
        default:
          return '';
      }
    },

    /**
     * Generates the warning message using translation keys and interpolation.
     *
     * @param {string} key - The warning type key (e.g., 'vatChange').
     * @param {object} warning - The structured warning data.
     * @returns {string} The warning message to display.
     */
    getWarningMessage(key, warning) {
      switch (key) {
        case 'vatChange':
          return this.$tkey('vatChange', {
            optimizedPrice: this.formatValue(warning.optimizedPrice),
            basePrice: this.formatValue(warning.basePrice),
            startVatRate: this.formatVatRate(warning.startVatRate),
            goLiveVatRate: this.formatVatRate(warning.goLiveVatRate),
          });

        case 'intentionPriceOverride':
          if (warning.intentionPrice === warning.optimizedPriceBeforeOverride) {
            return this.$tkey('intentionPriceOverride.noOverride', {
              intentionPrice: this.formatValue(warning.intentionPrice),
              referenceDateOrLivePrice: this.formatReferenceDateOrLivePrice(
                warning.referenceDateOrLivePrice
              ),
            });
          }
          return this.$tkey('intentionPriceOverride.override', {
            optimizedPriceBeforeOverride: this.formatValue(warning.optimizedPriceBeforeOverride),
            intentionPrice: this.formatValue(warning.intentionPrice),
            referenceDateOrLivePrice: this.formatReferenceDateOrLivePrice(
              warning.referenceDateOrLivePrice
            ),
          });

        case 'storeGroupExactMatch':
          if (warning.isLeading) {
            return this.$tkey('storeGroupExactMatch.leading', {
              storeGroupPrice: this.formatValue(warning.storeGroupPrice),
              ruleApplied: warning.ruleApplied ? ` rule '${warning.ruleApplied}'` : '',
            });
          }
          return this.$tkey('storeGroupExactMatch.derived', {
            leadingProductKeyDisplay: warning.leadingProductKeyDisplay,
            ruleApplied: warning.ruleApplied ? ` rule '${warning.ruleApplied}'` : '',
          });

        case 'exactCompetitorMatch':
          if (warning.isAnchor) {
            return this.$tkey('exactCompetitorMatch.anchor', {
              competitorName: warning.competitorName,
            });
          }
          return this.$tkey('exactCompetitorMatch.derived', {
            anchorProductKeyDisplay: warning.anchorProductKeyDisplay,
            competitorName: warning.competitorName,
          });

        case 'anchorProductSelected':
          return this.$tkey('anchorProductSelected.message', {
            productKeyDisplay: warning.productKeyDisplay,
            decidingFactor: this.formatDecidingFactor(warning.selectionCriteria),
          });

        case 'followingAnchorProduct':
          return this.$tkey('followingAnchorProduct.message', {
            anchorProductKeyDisplay: warning.anchorProductKeyDisplay,
          });

        case 'maxUpDownGuardrail':
          return this.$tkey('maxUpDownGuardrail', {
            cappedPrice: this.formatValue(warning.cappedPrice),
            limitApplied: this.$tkey(`limitApplied.${warning.limitApplied}`),
            limitValue: this.formatValue(warning.limitValue),
            limitType: this.$tkey(`limitType.${warning.limitType}`),
            referencePrice: this.formatValue(warning.referencePrice),
          });

        case 'storeGroupMaxUpDownGuardrail':
          return this.$tkey('storeGroupMaxUpDownGuardrail', {
            cappedPrice: this.formatValue(warning.cappedPrice),
            limitApplied: this.$tkey(`limitApplied.${warning.limitApplied}`),
            limitValue: this.formatValue(warning.limitValue),
            limitType: this.$tkey(`limitType.${warning.limitType}`),
            referencePrice: this.formatValue(warning.referencePrice),
            appliedRule: warning.appliedRule ? ` rule '${warning.appliedRule}'` : '',
          });

        default:
          // Fallback for unknown warning types
          return JSON.stringify(warning);
      }
    },
  },
};
</script>

<style scoped lang="scss">
@import '@style/base/_variables.scss';

.price-warnings {
  display: inline-flex;
  align-items: center;
  margin-left: 0.5rem;
}

.price-warnings-icon {
  cursor: pointer;
  color: $pricing-primary;
}
</style>

<style lang="scss">
.price-warnings-tooltip {
  background-color: rgba(0, 0, 0, 0.9) !important;
  padding: 1rem !important;

  .price-warnings-prefix {
    font-weight: 600;
    font-size: 14px !important;
    margin-bottom: 0.5rem;
  }

  .price-warnings-list {
    margin: 0;
    padding-left: 1.25rem;
    list-style-type: disc;
  }

  .price-warnings-item {
    font-size: 14px !important;
    line-height: 1.5;
    margin-bottom: 0.375rem;

    &:last-child {
      margin-bottom: 0;
    }
  }
}
</style>
